import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { Input } from '@actual-app/components/input';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type {
  PluggyAiAccount,
  SyncServerPluggyAiAccount,
} from '@actual-app/core/types/models';

import { Error as ErrorAlert } from '#components/alerts';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
} from '#components/common/Modal';
import { FormField, FormLabel } from '#components/forms';
import { useAccounts } from '#hooks/useAccounts';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { replaceModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

type PluggyAiConnectProps = Extract<
  ModalType,
  { name: 'pluggyai-connect' }
>['options'];

export const PluggyAiConnectModal = ({
  upgradingAccountId,
}: PluggyAiConnectProps = {}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: allAccounts = [] } = useAccounts();

  const existingConnections = useMemo(() => {
    const map = new Map<
      string,
      { bankId: string; bankName: string; linkedAccountIds: string[] }
    >();

    for (const acct of allAccounts) {
      if (acct.account_sync_source === 'pluggyai' && acct.bankId) {
        const existing = map.get(acct.bankId);
        if (existing) {
          if (acct.account_id) existing.linkedAccountIds.push(acct.account_id);
        } else {
          map.set(acct.bankId, {
            bankId: acct.bankId,
            bankName: acct.bankName || 'Pluggy Bank',
            linkedAccountIds: acct.account_id ? [acct.account_id] : [],
          });
        }
      }
    }

    return Array.from(map.values());
  }, [allAccounts]);

  const [connectionMode, setConnectionMode] = useState<'new' | 'existing'>(
    'new',
  );
  const [itemId, setItemId] = useState('');
  const [selectedBankId, setSelectedBankId] = useState(
    existingConnections[0]?.bankId || '',
  );
  const [showCustomCredentials, setShowCustomCredentials] = useState(false);
  const [customClientId, setCustomClientId] = useState('');
  const [customClientSecret, setCustomClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);

    const activeItemId =
      connectionMode === 'new' ? itemId.trim() : selectedBankId;

    if (!activeItemId) {
      setError(
        connectionMode === 'new'
          ? t('Please enter a Pluggy Item ID.')
          : t('Please select an existing bank connection.'),
      );
      return;
    }

    const trimmedCustomId = customClientId.trim();
    const trimmedCustomSecret = customClientSecret.trim();

    if (
      connectionMode === 'new' &&
      showCustomCredentials &&
      (trimmedCustomId || trimmedCustomSecret)
    ) {
      if (!trimmedCustomId || !trimmedCustomSecret) {
        setError(
          t(
            'When providing custom credentials, both Client ID and Client Secret are required.',
          ),
        );
        return;
      }
    }

    setIsLoading(true);

    try {
      const results = await send('pluggyai-accounts', {
        itemId: activeItemId,
        customCredentials:
          connectionMode === 'new' &&
          showCustomCredentials &&
          trimmedCustomId &&
          trimmedCustomSecret
            ? {
                clientId: trimmedCustomId,
                clientSecret: trimmedCustomSecret,
              }
            : undefined,
      });

      if (results?.error_code) {
        throw new Error(results.reason || results.error_code);
      }
      if (results?.error) {
        throw new Error(results.error);
      }
      if (results?.data?.error) {
        throw new Error(results.data.error);
      }

      const rawAccounts = (results?.data?.accounts ??
        results?.accounts ??
        []) as PluggyAiAccount[];

      let filteredAccounts = rawAccounts;
      if (connectionMode === 'existing') {
        const selectedConn = existingConnections.find(
          c => c.bankId === selectedBankId,
        );
        if (selectedConn) {
          filteredAccounts = rawAccounts.filter(
            a => !selectedConn.linkedAccountIds.includes(a.id),
          );
        }
      }

      if (filteredAccounts.length === 0) {
        throw new Error(
          t(
            'No available accounts found for this connection (or all accounts are already linked).',
          ),
        );
      }

      const institutionName =
        results?.data?.institution ?? results?.institution ?? 'Pluggy.ai';
      const connectionItemId =
        results?.data?.itemId ?? results?.itemId ?? activeItemId;

      const externalAccounts: SyncServerPluggyAiAccount[] =
        filteredAccounts.map(oldAccount => ({
          account_id: oldAccount.id,
          name: `${oldAccount.name.trim()} - ${
            oldAccount.type === 'BANK'
              ? oldAccount.taxNumber || oldAccount.number || t('Account')
              : oldAccount.owner || t('Credit Card')
          }`,
          institution: institutionName,
          orgDomain: null,
          orgId: connectionItemId,
          balance:
            oldAccount.type === 'BANK'
              ? (oldAccount.bankData?.automaticallyInvestedBalance || 0) +
                (oldAccount.bankData?.closingBalance || oldAccount.balance || 0)
              : oldAccount.balance || 0,
        }));

      dispatch(
        replaceModal({
          modal: {
            name: 'select-linked-accounts',
            options: {
              externalAccounts,
              syncSource: 'pluggyai',
              upgradingAccountId,
            },
          },
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      name="pluggyai-connect"
      containerProps={{ style: { width: '32vw' } }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Connect Bank via Pluggy.ai')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          <View style={{ display: 'flex', gap: 14 }}>
            {existingConnections.length > 0 && (
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <Button
                  variant={connectionMode === 'new' ? 'primary' : 'normal'}
                  onPress={() => {
                    setConnectionMode('new');
                    setError(null);
                  }}
                >
                  <Trans>New Pluggy connection</Trans>
                </Button>
                <Button
                  variant={connectionMode === 'existing' ? 'primary' : 'normal'}
                  onPress={() => {
                    setConnectionMode('existing');
                    setError(null);
                  }}
                >
                  <Trans>Use existing connection</Trans>
                </Button>
              </View>
            )}

            {connectionMode === 'existing' ? (
              <FormField>
                <FormLabel
                  title={t('Select an active Bank Connection:')}
                  htmlFor="pluggyai-existing-conn"
                />
                <Select
                  id="pluggyai-existing-conn"
                  options={existingConnections.map(c => [
                    c.bankId,
                    `${c.bankName} (${c.bankId})`,
                  ])}
                  value={selectedBankId}
                  onChange={val => setSelectedBankId(val)}
                />
                <Text
                  style={{
                    fontSize: 13,
                    color: theme.pageTextSubdued,
                    marginTop: 4,
                  }}
                >
                  <Trans>
                    Reuses the credentials and connection already active in this
                    budget.
                  </Trans>
                </Text>
              </FormField>
            ) : (
              <>
                <FormField>
                  <FormLabel
                    title={t('Pluggy Item ID:')}
                    htmlFor="pluggyai-item-id-field"
                  />
                  <InitialFocus>
                    <Input
                      id="pluggyai-item-id-field"
                      type="text"
                      placeholder="e.g. 74b6f1ca-..."
                      value={itemId}
                      onChangeValue={val => {
                        setItemId(val);
                        setError(null);
                      }}
                    />
                  </InitialFocus>
                  <Text
                    style={{
                      fontSize: 13,
                      color: theme.pageTextSubdued,
                      marginTop: 4,
                    }}
                  >
                    <Trans>
                      Enter the Item ID generated when authenticating with your
                      bank in Pluggy.
                    </Trans>
                  </Text>
                </FormField>

                <View style={{ alignItems: 'flex-start' }}>
                  <Button
                    variant="bare"
                    onPress={() =>
                      setShowCustomCredentials(!showCustomCredentials)
                    }
                    style={{ color: theme.pageTextSubdued, fontSize: 13 }}
                  >
                    {showCustomCredentials ? (
                      <Trans>▼ Hide custom credentials</Trans>
                    ) : (
                      <Trans>
                        ▶ Use custom credentials for this connection (optional)
                      </Trans>
                    )}
                  </Button>
                </View>

                {showCustomCredentials && (
                  <View
                    style={{
                      border: `1px solid ${theme.tableBorder}`,
                      borderRadius: 6,
                      padding: 12,
                      gap: 10,
                      backgroundColor: theme.tableBackground,
                    }}
                  >
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      <Trans>
                        Leave blank to use the budget default credentials. Only
                        provide credentials if this Item was created under a
                        different Pluggy developer application.
                      </Trans>
                    </Text>

                    <FormField>
                      <FormLabel
                        title={t('Custom Client ID:')}
                        htmlFor="pluggyai-custom-client-id"
                      />
                      <Input
                        id="pluggyai-custom-client-id"
                        type="text"
                        value={customClientId}
                        onChangeValue={setCustomClientId}
                      />
                    </FormField>

                    <FormField>
                      <FormLabel
                        title={t('Custom Client Secret:')}
                        htmlFor="pluggyai-custom-client-secret"
                      />
                      <Input
                        id="pluggyai-custom-client-secret"
                        type="password"
                        value={customClientSecret}
                        onChangeValue={setCustomClientSecret}
                      />
                    </FormField>
                  </View>
                )}
              </>
            )}

            {error && <ErrorAlert>{error}</ErrorAlert>}
          </View>

          <ModalButtons>
            <ButtonWithLoading
              variant="primary"
              isLoading={isLoading}
              onPress={onSubmit}
            >
              <Trans>Fetch accounts</Trans>
            </ButtonWithLoading>
          </ModalButtons>
        </>
      )}
    </Modal>
  );
};
